import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './MexicoResidency.css';
import axios from 'axios';
import { initializeTracker } from '../tracker_script.js';
import SEO from './SEO';

const MexicoResidency = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country: '',
    contact: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    submitted: false,
    error: null
  });
  const [honeypot, setHoneypot] = useState('');
  const formLoadTime = useRef(Date.now());

  useEffect(() => {
    // Add meta tags to head
    const usernameMeta = document.createElement('meta');
    usernameMeta.name = 'username';
    usernameMeta.content = 'user_2o6eptKfyuvEOe6kSTFVnGzxmMU';
    document.head.appendChild(usernameMeta);

    const offerMeta = document.createElement('meta');
    offerMeta.name = 'offer';
    offerMeta.content = 'Mexico Residency';
    document.head.appendChild(offerMeta);

    // Initialize the tracker
    try {
      initializeTracker();
      console.log('Tracker initialized successfully');
    } catch (error) {
      console.error('Error initializing tracker:', error);
    }

    // Cleanup function
    return () => {
      if (document.querySelector('meta[name="username"]')) {
        document.querySelector('meta[name="username"]').remove();
      }
      if (document.querySelector('meta[name="offer"]')) {
        document.querySelector('meta[name="offer"]').remove();
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Bot traps: honeypot filled or submitted too fast
    if (honeypot || Date.now() - formLoadTime.current < 3000) {
      return;
    }

    setFormStatus({ submitting: true, submitted: false, error: null });

    try {
      await axios.post('https://expater-server.vercel.app/api/mexico-contact', {
        ...formData,
        subject: 'Mexico Residency Inquiry'
      });
      
      // Redirect to thank you page instead of showing success message
      navigate('/mexico-thank-you');
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus({ 
        submitting: false, 
        submitted: false, 
        error: 'There was an error submitting your inquiry. Please try again later.'
      });
    }
  };

  return (
    <div className="mexico-residency-container">
      <SEO
        title="Mexico Residency & Citizenship Program"
        description="Get Mexico residency through economic solvency program. Professional assistance for Mexico temporary and permanent residency, pathway to Mexican citizenship in 2 years. Expert guidance for Mexico immigration and relocation."
        keywords="Mexico residency, Mexico citizenship, Mexico economic solvency, Mexico permanent residency, Mexico temporary residency, Mexico immigration, retire in Mexico, Mexico relocation, Mexican passport, Mexico visa"
        canonical="https://tuomaskivioja.com/mexico-residency"
        ogImage="https://tuomaskivioja.com/logo512.png"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Mexico Residency Program",
          "provider": {
            "@type": "Organization",
            "name": "Internet Made Media FZCO"
          },
          "description": "Professional assistance for Mexico residency through economic solvency program",
          "areaServed": "MX",
          "offers": {
            "@type": "Offer",
            "price": "1800",
            "priceCurrency": "USD",
            "description": "Professional assistance for Mexico residency application"
          },
          "url": "https://tuomaskivioja.com/mexico-residency"
        }}
      />
      <div className="mexico-header">
        <h1>Mexico Residency Program</h1>
        <div className="mexico-flag">🇲🇽</div>
      </div>

      <div className="mexico-content">
        <section className="mexico-info">
          <h2>Economic Solvency Residency Program</h2>

          <p>
            To qualify for residency through economic solvency you must demonstrate a minimum amount of income
            or savings/investments to qualify for residency in Mexico.
          </p>
          
          <div className="requirements-box">
            <h3>Qualification Requirements:</h3>
            <ul>
              <li>
                <strong>Income Option:</strong> Demonstrate about $4,400 of income per month for the past 6 months. 
                Active income is preferred, and you may be required to present documentation proving your employment.
              </li>
              <li>
                <strong>Savings Option:</strong> Demonstrate a balance of about $78,000 for the past 12 months 
                across all of your bank and brokerage accounts.
              </li>
            </ul>
            <p className="note">
              <em>These numbers vary from embassy to embassy, as well as the documents required to apply.</em>
            </p>
          </div>
          <p>
            In most cases, you will first need to obtain temporary residence and then renew it a few times, and after 4 years you can convert it to permanent residency.
          </p>
          <p>
            Professional help to navigate the process of getting residency in Mexico will make your life a lot easier 
            and will save you countless hours of dealing with government bureaucracy.
          </p>
          
          <div className="retirement-option">
            <h3>Retirement Option</h3>
            <p>
              There are also options for Residency through solvency specifically aimed at retired people,
              with higher minimums, but that offer permanent residency straight away.
            </p>
          </div>

          <div className="citizenship-path-box">
            <h3>Path to Citizenship 🇲🇽</h3>
            <p className="citizenship-intro">
              If you obtain permanent residency in Mexico it can lead to citizenship.
            </p>
            <ul className="citizenship-requirements">
              <li>You must first obtain permanent residency.</li>
              <li>You must then show that you lived 18 months of the prior 24 months inside Mexico.</li>
              <li>You must pass exams in Spanish and Mexican culture.</li>
            </ul>
          </div>
        </section>

        <section className="contact-form-section">
          <div className="pricing-box">
            <h3>💰 Professional Assistance - $1,800</h3>
            <ul className="pricing-benefits">
              <li>Document preparation</li>
              <li>Application screening</li>
              <li>Assistance with converting your entry visa to a residence card inside Mexico</li>
              <li>Translation of documents into Spanish by sworn translators in Mexico</li>
              <li>Assistance for passing the Mexican culture and Spanish exams (if you go for citizenship)</li>
            </ul>
            <p className="pricing-note">
              <em>Expect to pay an extra $350-$700 in government fees depending on the residency type</em>
            </p>
          </div>

          <h2>Contact Us About Mexico Residency</h2>
          <p>Fill out the form below to be connected to my Mexico immigration agent Sergei.</p>
          
          {formStatus.submitted ? (
            <div className="success-message">
              <h3>Thank you for your inquiry!</h3>
              <p>We've received your message and will contact you shortly about Mexico residency options.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mexico-contact-form">
              {/* Honeypot — hidden from real users, bots fill it in */}
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="country">Your Country of Citizenship *</label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="contact">WhatsApp/Telegram/Phone Number</label>
                <input
                  type="text"
                  id="contact"
                  name="contact"
                  value={formData.contact}
                  onChange={handleChange}
                  placeholder="Optional"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="message">Your Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  placeholder="Tell us about your situation and what you're looking to achieve with Mexican residency"
                  required
                ></textarea>
              </div>
              
              {formStatus.error && (
                <div className="error-message">
                  {formStatus.error}
                </div>
              )}
              
              <button 
                type="submit" 
                className="submit-btn"
                disabled={formStatus.submitting}
              >
                {formStatus.submitting ? 'Sending...' : 'Submit Inquiry'}
              </button>
            </form>
          )}
        </section>
      </div>
    </div>
  );
};

export default MexicoResidency;
